import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, ConfirmDialog, DataGrid, Modal, ShareMenu, StatusBadge, Thumbnail, useToast,
  type DataGridColumn,
} from '@kss/ui';
import { QrCode, RefreshCw, Share2, Trash2, Pencil } from 'lucide-react';
import { StandardAddDropdown } from '../shared/AddDropdown';
import type { Product } from '@kss/api';
import { productService } from '@kss/api';
import { useModulePerms } from '../../hooks/useModulePerms';
import { encodeId } from '../../utils/idCodec';
import { isUnreachableHost, productBuyNowUrl } from '../../utils/storefrontLinks';
import { NameCell } from '../../hooks/useNameMaps';

export default function AllProducts() {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const perms = useModulePerms('Product');
  const [rows, setRows] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [detailRow, setDetailRow] = useState<Product | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Product | null>(null);
  /* Share is a modal rather than an inline dropdown: a menu opened inside a
     grid cell is clipped by the row, and the modal doubles as a preview of
     the exact link being sent. */
  const [shareRow, setShareRow] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await productService.all();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load products';
      setError(msg);
      toastRef.current.error('Load failed', msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    if (!confirmDelete?.productId) return;
    setDeleting(true);
    try {
      await productService.delete(confirmDelete.productId);
      toast.success('Deleted', `${confirmDelete.name} removed`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error('Delete failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setDeleting(false);
    }
  };

  const columns: DataGridColumn<Product>[] = [
    {
      accessor: 'imageUrl',
      title: '',
      width: 52,
      sortable: false,
      render: (r) => (
        <Thumbnail src={r.imageUrl} name={r.name} seed={r.productId ?? r.sku} />
      ),
    },
    { accessor: 'sku', title: 'SKU', sortable: true, filterable: true, width: 140 },
    { accessor: 'name', title: 'Name', sortable: true, filterable: true },
    { accessor: 'brand', title: 'Brand', sortable: true, filterable: true, render: (r) => r.brand || '—' },
    { accessor: 'unit', title: 'Unit', sortable: true, render: (r) => r.unit || '—' },
    {
      accessor: 'sellingPrice',
      title: 'Selling (₹)',
      sortable: true,
      textAlign: 'right',
      render: (r) => Number(r.sellingPrice ?? 0).toLocaleString('en-IN'),
    },
    {
      accessor: 'mrpPrice',
      title: 'MRP (₹)',
      sortable: true,
      textAlign: 'right',
      render: (r) => Number(r.mrpPrice ?? 0).toLocaleString('en-IN'),
    },
    {
      accessor: 'isActive',
      title: 'Status',
      sortable: true,
      render: (r) => <StatusBadge status={r.isActive ? 'active' : 'inactive'} />,
    },
    {
      accessor: 'productId',
      title: 'Actions',
      width: 110,
      render: (r) => (
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover"
            onClick={(e) => {
              e.stopPropagation();
              setShareRow(r);
            }}
            title="Share with payment link"
          >
            <Share2 className="h-3.5 w-3.5" />
          </button>
          {perms.canUpdate && (
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover"
              onClick={(e) => {
                e.stopPropagation();
                if (r.productId) navigate(`/panel/products/${encodeId(r.productId)}`);
              }}
              title="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {perms.canDelete && (
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded text-danger hover:bg-surface-card-hover"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDelete(r);
              }}
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="-m-6 p-[3px]">
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <DataGrid<Product>
        title={<span className="hidden sm:inline">All products</span>}
        columnPicker
        onRowClick={(row) => setDetailRow(row)}
        data={rows}
        columns={columns}
        selectable
        selectedIds={selected}
        onSelectedChange={setSelected}
        defaultPageSize={10}
        pageSizes={[5, 10, 25, 50, 100]}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            {/* Bulk QR print — only surfaces once one or more rows are
                selected. Navigates to the print sheet with the ids in
                router state; falls back to a `?ids=` query string so
                a full page reload of the sheet still works. */}
            {selected.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const ids = selected.map(String);
                  navigate(
                    `/panel/products/qr-print?ids=${encodeURIComponent(ids.join(','))}`,
                    { state: { productIds: ids } },
                  );
                }}
              >
                <QrCode className="h-3.5 w-3.5" /> Print QR ({selected.length})
              </Button>
            )}
            <StandardAddDropdown
              basePath="/panel/products"
              newLabel="New product"
              service={productService}
              templateFields={['sku', 'name', 'brand', 'unit', 'costPrice', 'sellingPrice', 'mrpPrice', 'taxRate', 'weight', 'barcode', 'hsnCode', 'imageUrl', 'description', 'categoryId', 'storeId', 'isActive']}
            />
          </div>
        }
      />

      {/* Share sheet — shows the exact link before it goes out. */}
      <Modal
        open={!!shareRow}
        onClose={() => setShareRow(null)}
        title={shareRow ? `Share · ${shareRow.name}` : ''}
        width={520}
      >
        {shareRow?.productId && (() => {
          const link = productBuyNowUrl(shareRow.productId);
          const unreachable = isUnreachableHost(link);
          return (
            <div className="grid gap-3">
              <p className="text-sm text-content-secondary">
                Sends a link that adds this product to the customer's cart and takes them
                straight to checkout.
              </p>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-content-secondary">
                  Payment link
                </span>
                <input
                  readOnly
                  value={link}
                  onFocus={(e) => e.currentTarget.select()}
                  className="h-9 w-full rounded-md border border-border bg-surface-card-hover px-2 text-xs text-content-primary"
                />
              </label>
              {unreachable && (
                <Alert variant="warning">
                  This link points at a local address, so it won't open on anyone else's
                  device. Set <code>window.__KSS_CONFIG__.storefrontOrigin</code> in
                  runtime-config.js to the public storefront URL.
                </Alert>
              )}
              <div>
                <ShareMenu
                  content={{
                    title: shareRow.name ?? 'Product',
                    message: `${shareRow.name ?? 'Product'}${
                      shareRow.sellingPrice != null
                        ? ` — ₹${Number(shareRow.sellingPrice).toLocaleString('en-IN')}`
                        : ''
                    }`,
                    url: link,
                  }}
                  label="Send via…"
                  warning={unreachable ? 'Link is not publicly reachable' : null}
                  onResult={(_c, result) => {
                    if (result === 'copied') toastRef.current.success('Link copied');
                    if (result === 'unsupported') {
                      toastRef.current.error('Sharing unavailable', 'Try copying the link.');
                    }
                  }}
                />
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        title={detailRow ? `Product · ${detailRow.name}` : ''}
        width={560}
        footer={
          <>
            <Button variant="outline" onClick={() => setDetailRow(null)}>Close</Button>
            {detailRow?.productId && (
              <Button onClick={() => { const id = detailRow.productId!; setDetailRow(null); navigate(`/panel/products/${encodeId(id)}`); }}>
                Edit
              </Button>
            )}
          </>
        }
      >
        {detailRow && (
          <div className="grid gap-3 rounded-md border border-border bg-surface-card-hover p-4 text-sm">
            <div className="flex items-start gap-3">
              <Thumbnail
                src={detailRow.imageUrl}
                name={detailRow.name}
                seed={detailRow.productId ?? detailRow.sku}
                className="h-20 w-20"
              />
              <div className="min-w-0 flex-1 text-xs">
                <div className="font-semibold text-content-primary">{detailRow.name}</div>
                <div className="text-content-muted">SKU {detailRow.sku}</div>
                {detailRow.brand && <div className="text-content-muted">Brand: {detailRow.brand}</div>}
                {detailRow.description && (
                  <p className="mt-1 line-clamp-3 text-content-secondary">{detailRow.description}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <DetailRow label="Cost" value={`₹ ${Number(detailRow.costPrice ?? 0).toLocaleString('en-IN')}`} />
              <DetailRow label="Selling" value={`₹ ${Number(detailRow.sellingPrice ?? 0).toLocaleString('en-IN')}`} />
              <DetailRow label="MRP" value={`₹ ${Number(detailRow.mrpPrice ?? 0).toLocaleString('en-IN')}`} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <DetailRow label="Tax" value={`${detailRow.taxRate ?? 0}%`} />
              <DetailRow label="Unit" value={detailRow.unit} />
              <DetailRow label="Weight" value={`${detailRow.weight ?? 0} kg`} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <DetailRow label="Barcode" value={detailRow.barcode} />
              <DetailRow label="HSN" value={detailRow.hsnCode} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {/* Resolve FK IDs → names via NameCell so the detail
                  modal never shows raw GUIDs to admins. */}
              <DetailRow label="Category" value={<NameCell kind="category" id={detailRow.categoryId} />} />
              <DetailRow label="Store" value={<NameCell kind="store" id={detailRow.storeId} />} />
            </div>
            <DetailRow
              label="Status"
              value={<StatusBadge status={detailRow.isActive ? 'active' : 'inactive'} />}
            />
            {detailRow.hasVariants && (
              <DetailRow
                label="Variants"
                value={`${detailRow.variants?.length ?? 0} variant(s)`}
              />
            )}
            {(detailRow.productAttributes?.length ?? 0) > 0 && (
              <DetailRow
                label="Attributes"
                value={`${detailRow.productAttributes?.length ?? 0} attribute(s)`}
              />
            )}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => (deleting ? undefined : setConfirmDelete(null))}
        onConfirm={handleDelete}
        title="Delete product"
        message={
          confirmDelete
            ? `This will permanently remove "${confirmDelete.name}" (${confirmDelete.sku}). This action cannot be undone.`
            : ''
        }
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        variant="danger"
      />
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-surface-card px-3 py-2">
      <span className="text-xs text-content-muted">{label}</span>
      <span className="text-sm font-medium text-content-primary">
        {value || <em className="text-content-muted">—</em>}
      </span>
    </div>
  );
}
